// AdminUsersPage — wired to real backend.
//
// BE (ISSUE-44) supports full CRUD for admin users: create, list, get,
// update profile, soft-delete, plus role assign/revoke and password reset.
// The "+ Yeni Yönetici" button opens the create drawer.

import { useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Segmented,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Shield, ShieldOff } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { adminUsersApi } from '@api/adminUsers';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS, ROLE, type Role } from '@shared/rbac/permissions.const';
import { useRoleLabel } from '@shared/constants/labels';
import { AdminUserDrawer } from '../components/AdminUserDrawer';
import type { AdminUser, AdminUserStatus } from '@features/auth/types';

const ROLE_COLOR: Record<Role, string> = {
  ADMIN: 'red',
  EDITOR: 'blue',
  WRITER: 'green',
};

export function AdminUsersPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [role, setRole] = useState<'ALL' | Role>('ALL');
  const [status, setStatus] = useState<'ALL' | AdminUserStatus>('ALL');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const roleLabel = useRoleLabel();
  const { t } = useTranslation(['users', 'common']);
  const confirm = useConfirm();
  // BIZ-SEC-04 — 2FA reset, settings.manage'den AYRI security.manage gate'inde.
  const canResetTwoFactor = useCan(PERMISSIONS.SECURITY_MANAGE);

  const { data, isFetching } = useQuery({
    queryKey: [
      'admin-users',
      { page, pageSize, q: debouncedSearch, role, status },
    ],
    queryFn: () =>
      adminUsersApi.list({ page, pageSize, q: debouncedSearch, role, status }),
    placeholderData: (prev) => prev,
  });

  const resetTwoFactorMutation = useMutation({
    mutationFn: (id: string) => adminUsersApi.resetTwoFactor(id),
    onSuccess: async () => {
      toast.success(t('admin.twoFactor.resetSuccess'));
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const columns = useMemo(
    () => [
      {
        key: 'user',
        title: t('admin.table.columns.user'),
        dataIndex: 'fullName',
        render: (_: unknown, row: AdminUser) => (
          <Space>
            <Avatar
              size={32}
              src={resolveAssetUrl(row.avatarUrl) ?? undefined}
              style={{
                background: 'var(--ds-brand)',
                color: 'var(--ds-text-on-brand)',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {row.fullName
                .split(' ')
                .map((p) => p[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </Avatar>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontWeight: 600 }}>{row.fullName}</span>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
                {row.email}
              </span>
            </div>
          </Space>
        ),
      },
      {
        key: 'roles',
        title: t('admin.table.columns.beRoles'),
        dataIndex: 'roles',
        responsive: ['md' as const],
        render: (_: unknown, row: AdminUser) => (
          <Space wrap size={4}>
            {(row.roles ?? []).map((r) => (
              <Tag key={r} color="purple">
                {r}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        key: 'role',
        title: t('admin.table.columns.feRole'),
        dataIndex: 'role',
        width: 140,
        render: (r: Role) => (
          <Tag color={ROLE_COLOR[r]} icon={<Shield size={12} style={{ verticalAlign: -1 }} />}>
            {roleLabel(r)}
          </Tag>
        ),
      },
      {
        key: 'status',
        title: t('common:labels.status'),
        dataIndex: 'status',
        width: 100,
        render: (s: AdminUserStatus) =>
          s === 'active' ? (
            <Tag color="green">{t('admin.status.active')}</Tag>
          ) : (
            <Tag color="default">{t('admin.status.inactive')}</Tag>
          ),
      },
      {
        key: 'createdAt',
        title: t('common:labels.createdAt'),
        dataIndex: 'createdAt',
        width: 130,
        responsive: ['md' as const],
        render: (v: string) =>
          v ? new Date(v).toLocaleDateString('tr-TR') : '—',
      },
      {
        key: 'actions',
        title: '',
        width: 80,
        render: (_: unknown, row: AdminUser) => {
          const isSelf = row.id === me?.id;
          return (
            <Space size={4}>
              <Tooltip title={isSelf ? t('admin.tooltip.selfRole') : t('admin.tooltip.manageRole')}>
                <Button
                  size="small"
                  icon={<Pencil size={14} />}
                  disabled={isSelf}
                  onClick={() => {
                    setEditing(row);
                    setDrawerOpen(true);
                  }}
                />
              </Tooltip>
              {canResetTwoFactor && !isSelf ? (
                <Tooltip title={t('admin.twoFactor.resetTitle')}>
                  <Button
                    size="small"
                    danger
                    icon={<ShieldOff size={14} />}
                    loading={
                      resetTwoFactorMutation.isPending &&
                      resetTwoFactorMutation.variables === row.id
                    }
                    onClick={() => {
                      void (async () => {
                        const ok = await confirm({
                          title: t('admin.twoFactor.resetTitle'),
                          description: t('admin.twoFactor.resetConfirm', { name: row.fullName }),
                          okText: t('admin.twoFactor.resetOk'),
                        });
                        if (ok) resetTwoFactorMutation.mutate(row.id);
                      })();
                    }}
                    aria-label={t('admin.twoFactor.resetTitle')}
                  />
                </Tooltip>
              ) : null}
            </Space>
          );
        },
      },
    ],
    [me?.id, roleLabel, t, canResetTwoFactor, resetTwoFactorMutation, confirm],
  );

  return (
    <div>
      <PageHeader
        title={t('admin.page.title')}
        description={t('admin.page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('admin.breadcrumb.users') },
          { label: t('admin.breadcrumb.list') },
        ]}
        actions={
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            {t('admin.actions.new')}
          </Button>
        }
      />

      <DataTable<AdminUser>
        data={data?.items ?? []}
        columns={columns}
        rowKey="id"
        loading={isFetching}
        search={{
          value: search,
          onChange: (v) => {
            setSearch(v);
            setPage(1);
          },
          placeholder: t('admin.search.placeholder'),
        }}
        filters={
          <Space wrap>
            <Segmented
              size="small"
              value={role}
              onChange={(v) => {
                setRole(v as 'ALL' | Role);
                setPage(1);
              }}
              options={[
                { value: 'ALL', label: t('admin.filters.allRoles') },
                { value: ROLE.ADMIN, label: roleLabel(ROLE.ADMIN) },
                { value: ROLE.EDITOR, label: roleLabel(ROLE.EDITOR) },
                { value: ROLE.WRITER, label: roleLabel(ROLE.WRITER) },
              ]}
            />
            <Segmented
              size="small"
              value={status}
              onChange={(v) => {
                setStatus(v as 'ALL' | AdminUserStatus);
                setPage(1);
              }}
              options={[
                { value: 'ALL', label: t('common:labels.all') },
                { value: 'active', label: t('admin.status.active') },
                { value: 'inactive', label: t('admin.status.inactive') },
              ]}
            />
          </Space>
        }
        pagination={{
          page,
          size: pageSize,
          total: data?.total ?? 0,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        exportable
        exportFileName="admin-users"
      />

      <AdminUserDrawer
        open={drawerOpen}
        mode={editing ? 'edit' : 'create'}
        user={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['admin-users'] })}
      />
    </div>
  );
}
